import { createElement, PAGE_WIDTH, PAGE_HEIGHT } from '../templateDocument.js'

export const PDFJS_VERSION = '6.3.289'
export const MAX_PDF_BYTES = 20 * 1024 * 1024
export const MAX_PDF_PAGES = 30
export const MAX_PAGE_IMAGE_BYTES = 2 * 1024 * 1024
export const MAX_DOCUMENT_IMAGE_BYTES = 8 * 1024 * 1024
export const MAX_PDF_OPERATIONS = 100000
const MM_PER_POINT = 25.4 / 72
const round = (value) => Math.round(value * 100) / 100

export function copyPdfBytes(bytes) {
  const input = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes
  if (!(input instanceof Uint8Array) || !input.length)
    throw new Error('Selecione um arquivo PDF válido.')
  if (input.byteLength > MAX_PDF_BYTES)
    throw new Error('O PDF pode ter até 20 MB.')
  const header = Array.from(input.subarray(0, 1024), (byte) =>
    String.fromCharCode(byte),
  ).join('')
  if (!header.includes('%PDF-'))
    throw new Error('O arquivo selecionado não contém um PDF válido.')
  return input.slice()
}

export function fitPdfPage(viewport) {
  const { width, height } = viewport
  if (![width, height].every((value) => Number.isFinite(value) && value > 0))
    throw new Error('O PDF contém uma página com dimensões inválidas.')
  const scale = Math.min(PAGE_WIDTH / width, PAGE_HEIGHT / height)
  const outputWidth = width * scale
  const outputHeight = height * scale
  return {
    scale,
    x: (PAGE_WIDTH - outputWidth) / 2,
    y: (PAGE_HEIGHT - outputHeight) / 2,
    width: outputWidth,
    height: outputHeight,
    isA4:
      Math.abs(width * MM_PER_POINT - PAGE_WIDTH) <= 1 &&
      Math.abs(height * MM_PER_POINT - PAGE_HEIGHT) <= 1,
  }
}

export function pageImageBudget(usedBytes, remainingPages) {
  if (!Number.isInteger(remainingPages) || remainingPages < 1 || usedBytes < 0)
    throw new Error('Orçamento de imagens inválido.')
  return Math.min(
    MAX_PAGE_IMAGE_BYTES,
    Math.floor((MAX_DOCUMENT_IMAGE_BYTES - usedBytes) / remainingPages),
  )
}

export function mapPdfFont(font) {
  if (
    !font ||
    typeof font !== 'object' ||
    font.isType3Font ||
    font.isInvalidPDFjsFont ||
    font.vertical
  )
    return null
  const sourceName = String(font.name || '')
    .replace(/^[A-Z]{6}\+/, '')
    .trim()
  const name = sourceName.replace(/[\s,_-]/g, '')
  const fallback = String(font.fallbackName || '').toLowerCase()
  if (!name && !['serif', 'sans-serif', 'monospace'].includes(fallback))
    return null
  let fontFamily
  if (/^(Times(?:NewRoman)?|TimesRoman|LiberationSerif|NimbusRoman)/i.test(name))
    fontFamily = 'Times New Roman'
  else if (/^(Arial|Helvetica|LiberationSans|NimbusSans)/i.test(name))
    fontFamily = 'Arial'
  else if (/^(Calibri|Carlito)/i.test(name)) fontFamily = 'Calibri'
  // Only the bundled Latin/Greek/Cyrillic family and its known style suffixes
  // are equivalent. NotoSansCJK, NotoSansMono, Arabic, etc. remain substitutes.
  else if (/^NotoSans(?:(?:Regular|Roman|Normal|Book|Bold|Italic|Oblique|SemiBold|DemiBold|Medium|Light|Thin|Black|Heavy|Condensed|Narrow|Expanded|ExtraBold|ExtraLight))*$/i.test(name))
    fontFamily = 'Noto Sans'
  const approximate =
    !fontFamily ||
    /condensed|narrow|expanded|light|thin|black|heavy/i.test(name) ||
    (fontFamily === 'Noto Sans' && /semibold|demibold|medium|extrabold/i.test(name)) ||
    font.black === true
  if (!fontFamily) {
    // Editable import deliberately substitutes ordinary fonts that the editor
    // cannot store. PDF.js supplies only a generic fallback family here; this
    // preserves the broad serif/sans appearance, not the source font metrics.
    const serif =
      /^(Cambria|Caladea|Georgia|Garamond|EBGaramond|BookAntiqua|Palatino|Baskerville|MADESaonara|Cormorant)/i.test(
        name,
      ) || fallback === 'serif'
    fontFamily = serif ? 'Times New Roman' : 'Arial'
  }
  return {
    fontFamily,
    bold:
      font.bold === true ||
      font.black === true ||
      /bold|demi|semibold|black|heavy/i.test(name),
    italic: font.italic === true || /italic|oblique/i.test(name),
    ...(approximate
      ? {
          warning: `A fonte "${(sourceName || fallback).replace(/\s+/g, ' ').slice(0, 120)}" foi substituída por ${fontFamily} para manter o texto editável. Confira a aparência e as quebras de linha.`,
        }
      : {}),
  }
}

function fillColor(name, args) {
  const values = Array.from(args || [])
  if (values.length === 1 && /^#[\da-f]{6}$/i.test(values[0]))
    return values[0].toLowerCase()
  const channels =
    name === 'setFillGray' && values.length === 1
      ? [values[0], values[0], values[0]]
      : values
  if (
    channels.length !== 3 ||
    !channels.every(
      (value) => Number.isFinite(value) && value >= 0 && value <= 1,
    )
  )
    return null
  return `#${channels
    .map((value) =>
      Math.round(value * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`
}

export const TEXT_PAINT_OPERATORS = [
  'showText',
  'showSpacedText',
  'nextLineShowText',
  'nextLineSetSpacingShowText',
]

// Editable import is allowed only when removing glyph painting cannot affect a
// clipping path, transparency group, annotation or the stacking of later artwork.
export function analyzePdfOperators(
  operatorList,
  OPS,
  { textContent, viewport } = {},
) {
  const names = new Map(Object.entries(OPS).map(([name, id]) => [id, name]))
  const operations = operatorList?.fnArray || []
  if (!operations.length || operations.length > MAX_PDF_OPERATIONS)
    return {
      reason:
        'a página contém uma estrutura gráfica extensa ou não reconhecida',
    }
  const blocked = new Set([
    'beginGroup',
    'endGroup',
    'beginAnnotation',
    'paintFormXObjectBegin',
    'paintFormXObjectEnd',
    'setFillColor',
    'setFillColorN',
    'setFillColorSpace',
    'setFillCMYKColor',
    'setFillTransparent',
    'paintImageMaskXObject',
    'paintImageMaskXObjectGroup',
    'paintImageMaskXObjectRepeat',
    'paintSolidColorImageMask',
    'setCharWidth',
    'setCharWidthAndBounds',
  ])
  const textPaint = new Set(TEXT_PAINT_OPERATORS)
  const paint =
    /^(?:stroke|closeStroke|fill|eoFill|fillStroke|eoFillStroke|closeFillStroke|closeEOFillStroke|shadingFill|paint|constructPath|rawFillPath)/
  let textStarted = false
  let pendingClip = false
  let hasArtwork = false
  let paintedText = ''
  const paintedSegments = []
  let state = {
    color: '#000000',
    clipped: false,
    transform: [1, 0, 0, 1, 0, 0],
    lineWidth: 1,
  }
  const stack = []
  const textBounds =
    viewport && textContent ? getTextBounds(textContent, viewport) : null
  for (let index = 0; index < operations.length; index += 1) {
    const name = names.get(operations[index])
    const args = operatorList.argsArray[index] || []
    if (!name || blocked.has(name))
      return {
        reason:
          'há recortes, transparência, máscaras ou recursos gráficos incompatíveis com texto editável',
      }
    if (name === 'save')
      stack.push({ ...state, transform: [...state.transform] })
    if (name === 'restore') {
      if (!stack.length)
        return { reason: 'a estrutura de estados gráficos está incompleta' }
      state = stack.pop()
    }
    if (name === 'transform') {
      if (args.length !== 6 || !args.every(Number.isFinite))
        return { reason: 'há uma transformação gráfica inválida' }
      state.transform = multiply(state.transform, args)
    }
    if (name === 'setLineWidth') state.lineWidth = args[0]
    if (['clip', 'eoClip'].includes(name)) pendingClip = true
    if (name === 'constructPath' && pendingClip) {
      // PDF.js applies the pending clip when this path is consumed. A page-wide
      // rectangle is harmless only after verifying its actual vertices and that
      // every complete text box lies inside it; a path's bounding box alone
      // cannot exclude holes, diagonal cuts or nested clipping regions.
      state.clipped ||= !clipContainsEveryText(
        args,
        state,
        viewport,
        textBounds,
      )
      pendingClip = false
    }
    if (name === 'setGState') {
      const entries = args[0]
      if (
        !Array.isArray(entries) ||
        !entries.every(
          ([key, value]) =>
            (['ca', 'CA'].includes(key) && value === 1) ||
            (key === 'BM' && ['Normal', 'source-over'].includes(value)) ||
            (key === 'SMask' && (value === false || value === 'None')),
        )
      )
        return {
          reason:
            'há transparência ou um estado gráfico que exige preservar a aparência',
        }
    }
    if (name === 'beginMarkedContentProps' && args[0] === 'OC')
      return {
        reason: 'há camadas opcionais cuja visibilidade precisa ser preservada',
      }
    if (name === 'setTextRenderingMode' && args[0] !== 0)
      return { reason: 'há texto invisível, contornado ou usado como recorte' }
    if (
      ['setCharSpacing', 'setWordSpacing', 'setTextRise'].includes(name) &&
      args[0] !== 0
    )
      return {
        reason:
          'há espaçamento ou deslocamento de texto que o editor não representa',
      }
    if (name === 'setHScale' && args[0] !== 100)
      return { reason: 'há texto com escala horizontal especial' }
    if (['setFillRGBColor', 'setFillGray'].includes(name))
      state.color = fillColor(name, args)
    if (textPaint.has(name)) {
      if (state.clipped || pendingClip)
        return { reason: 'há recortes aplicados ao texto' }
      if (!state.color)
        return {
          reason: 'a cor do texto não pôde ser convertida com segurança',
        }
      textStarted = true
      const glyphs = name === 'nextLineSetSpacingShowText' ? args[2] : args[0]
      if (!Array.isArray(glyphs))
        return {
          reason: 'a codificação dos caracteres não pôde ser verificada',
        }
      let segmentText = ''
      for (const glyph of glyphs) {
        if (typeof glyph === 'number') continue
        if (!glyph || typeof glyph.unicode !== 'string')
          return {
            reason: 'a codificação dos caracteres não pôde ser verificada',
          }
        paintedText += glyph.unicode
        segmentText += glyph.unicode
      }
      paintedSegments.push({ text: segmentText, color: state.color })
    } else if (
      paint.test(name) &&
      !(
        name === 'constructPath' &&
        OPS.endPath !== undefined &&
        args[0] === OPS.endPath
      )
    ) {
      if (textStarted) {
        const bounds = artworkBounds(name, args, state, viewport)
        if (
          !bounds ||
          !textBounds ||
          textBounds.some((text) => intersects(bounds, text))
        )
          return {
            reason:
              'há imagens ou desenhos sobrepostos ao texto, ou sem posição verificável',
          }
      }
      hasArtwork = true
    }
  }
  return { reason: null, hasArtwork, paintedText, paintedSegments }
}

const comparableText = (text) => text.normalize('NFKC').replace(/\s+/gu, '')
const multiply = (a, b) => [
  a[0] * b[0] + a[2] * b[1],
  a[1] * b[0] + a[3] * b[1],
  a[0] * b[2] + a[2] * b[3],
  a[1] * b[2] + a[3] * b[3],
  a[0] * b[4] + a[2] * b[5] + a[4],
  a[1] * b[4] + a[3] * b[5] + a[5],
]

function transformBounds(bounds, transform, padding = 0) {
  if (
    !bounds ||
    bounds.length !== 4 ||
    !Array.from(bounds).every(Number.isFinite) ||
    !Number.isFinite(padding)
  )
    return null
  const [x1, y1, x2, y2] = bounds
  const points = [
    [x1, y1],
    [x1, y2],
    [x2, y1],
    [x2, y2],
  ].map(([x, y]) => [
    transform[0] * x + transform[2] * y + transform[4],
    transform[1] * x + transform[3] * y + transform[5],
  ])
  if (!points.flat().every(Number.isFinite)) return null
  return {
    left: Math.min(...points.map(([x]) => x)) - padding,
    top: Math.min(...points.map(([, y]) => y)) - padding,
    right: Math.max(...points.map(([x]) => x)) + padding,
    bottom: Math.max(...points.map(([, y]) => y)) + padding,
  }
}

function getTextBounds(textContent, viewport) {
  const output = []
  const scale = Math.hypot(viewport.transform[0], viewport.transform[1])
  for (const item of textContent.items || []) {
    if (!item.str?.trim()) continue
    if (
      !Array.isArray(item.transform) ||
      item.transform.length !== 6 ||
      !item.transform.every(Number.isFinite)
    )
      return null
    const tx = multiply(viewport.transform, item.transform)
    const height = Math.hypot(tx[2], tx[3])
    if (
      !Number.isFinite(item.width) ||
      Math.abs(tx[1]) > 0.01 ||
      Math.abs(tx[2]) > 0.01
    )
      return null
    const style = textContent.styles?.[item.fontName]
    const descent =
      Number.isFinite(style?.descent) && style.descent < 0
        ? Math.abs(style.descent)
        : 0.4
    const ascent =
      Number.isFinite(style?.ascent) && style.ascent > 0
        ? Math.max(1, style.ascent)
        : 1
    // Include ascenders, descenders and a tolerance; proving nonintersection is
    // deliberately stricter than the tight glyph box used for text placement.
    // Embedded font metrics avoid mistaking a paragraph border below all glyphs
    // for an overlapping drawing. Unknown metrics retain the wider fallback.
    output.push({
      left: tx[4] - 1,
      right: tx[4] + item.width * scale + 1,
      top: tx[5] - height * ascent - 1,
      bottom: tx[5] + height * descent + 1,
    })
  }
  return output
}

function artworkBounds(name, args, state, viewport) {
  if (!viewport) return null
  const transform = multiply(viewport.transform, state.transform)
  if (name === 'paintImageXObject' || name === 'paintInlineImageXObject')
    return transformBounds([0, 0, 1, 1], transform, 1)
  if (name === 'constructPath') {
    const scale = Math.max(
      Math.hypot(transform[0], transform[1]),
      Math.hypot(transform[2], transform[3]),
    )
    return transformBounds(
      args[2],
      transform,
      Math.abs(state.lineWidth) * scale + 1,
    )
  }
  return null
}

function clipContainsEveryText(args, state, viewport, textBounds) {
  if (!viewport || !textBounds?.length || args[1]?.length !== 1) return false
  const path = args[1][0]
  // DrawOPS in the pinned PDF.js: moveTo=0, lineTo=1, closePath=4.
  if (
    (!Array.isArray(path) && !ArrayBuffer.isView(path)) ||
    path.length !== 13 ||
    path[0] !== 0 ||
    path[3] !== 1 ||
    path[6] !== 1 ||
    path[9] !== 1 ||
    path[12] !== 4
  )
    return false
  const points = [0, 3, 6, 9].map((index) => [path[index + 1], path[index + 2]])
  if (!points.flat().every(Number.isFinite)) return false
  const xs = new Set(points.map(([x]) => x))
  const ys = new Set(points.map(([, y]) => y))
  if (
    xs.size !== 2 ||
    ys.size !== 2 ||
    new Set(points.map((point) => point.join(','))).size !== 4 ||
    points.some(([x, y], index) => {
      const next = points[(index + 1) % 4]
      return x !== next[0] && y !== next[1]
    })
  )
    return false
  const transform = multiply(viewport.transform, state.transform)
  if (Math.abs(transform[1]) > 0.000001 || Math.abs(transform[2]) > 0.000001)
    return false
  const bounds = transformBounds(
    [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)],
    transform,
  )
  return (
    bounds !== null &&
    textBounds.every(
      (text) =>
        text.left >= bounds.left &&
        text.right <= bounds.right &&
        text.top >= bounds.top &&
        text.bottom <= bounds.bottom,
    )
  )
}

function intersects(a, b) {
  return (
    a.left <= b.right &&
    a.right >= b.left &&
    a.top <= b.bottom &&
    a.bottom >= b.top
  )
}

export function buildEditablePdfElements({
  textContent,
  viewport,
  fit,
  fonts,
  paintedText,
  paintedSegments,
  reserveBackground = false,
}) {
  const items = (textContent?.items || []).filter(
    (item) => typeof item.str === 'string' && item.str.length,
  )
  if (!items.some((item) => item.str.trim()))
    return {
      reason:
        'não foi encontrado texto selecionável; a página pode ser digitalizada',
    }
  const sourceText = items.map((item) => item.str).join('')
  if (comparableText(paintedText) !== comparableText(sourceText))
    return {
      reason: 'o texto extraído difere dos caracteres desenhados na página',
    }
  const warnings = new Set()
  const editableText = new Map()
  for (const item of items) {
    const fontName = String(fonts.get(item.fontName)?.name || '')
      .replace(/^[A-Z]{6}\+/, '')
      .replace(/[\s,_-]/g, '')
    // Legacy Microsoft Symbol bullets use the private U+F0B7 slot. In the
    // pinned PDF.js SymbolSetEncoding, 0xB7 is "bullet", whose Unicode mapping
    // is U+2022. Normalize this one verified marker only after the original
    // extracted and painted text have matched; all other private glyphs fail.
    const text = /^Symbol(?:MT)?$/i.test(fontName)
      ? item.str.replace(/\uf0b7/g, '\u2022')
      : item.str
    editableText.set(item, text)
    if (text !== item.str)
      warnings.add(
        'Marcadores de lista da fonte Symbol foram convertidos para o caractere • para permanecerem editáveis.',
      )
  }
  const reconstructedText = [...editableText.values()].join('')
  const unsupportedCharacter = Array.from(reconstructedText).some(
    (character) => {
      const code = character.codePointAt(0)
      return (
        (code < 32 && ![9, 10, 13].includes(code)) ||
        code === 0xfffd ||
        // Private-use code points have meaning only in their original font. A
        // supported substitute cannot reproduce those glyphs reliably.
        (code >= 0xe000 && code <= 0xf8ff) ||
        (code >= 0xf0000 && code <= 0xffffd) ||
        (code >= 0x100000 && code <= 0x10fffd)
      )
    },
  )
  if (unsupportedCharacter || /<[!/a-zA-Z][^>]*>/.test(sourceText))
    return {
      reason:
        'há caracteres ou conteúdo que não podem ser representados como texto simples',
    }
  const unitScale = Math.hypot(viewport.transform[0], viewport.transform[1])
  const colors = paintedSegments?.flatMap((segment) =>
    Array.from(comparableText(segment.text), () => segment.color),
  )
  let characterIndex = 0
  const runs = []
  for (const item of items) {
    if (!item.str.trim()) continue
    const characterCount = Array.from(comparableText(item.str)).length
    const runColors = colors?.slice(
      characterIndex,
      characterIndex + characterCount,
    )
    characterIndex += characterCount
    if (
      runColors &&
      (runColors.length !== characterCount || new Set(runColors).size !== 1)
    )
      return {
        reason:
          'um trecho contém cores combinadas que não podem ser separadas sem alterar o texto',
      }
    if (
      !Array.isArray(item.transform) ||
      item.transform.length !== 6 ||
      !item.transform.every(Number.isFinite)
    )
      return { reason: 'há posições de texto inválidas' }
    const tx = multiply(viewport.transform, item.transform)
    const height = Math.hypot(tx[2], tx[3])
    const width = item.width * unitScale
    const font = mapPdfFont(fonts.get(item.fontName))
    const style = textContent.styles?.[item.fontName]
    if (!font)
      return {
        reason:
          'uma fonte está inválida ou não possui informações suficientes para reconstruir o texto',
      }
    const { warning, ...mappedFont } = font
    if (warning) warnings.add(warning)
    if (
      item.dir !== 'ltr' ||
      style?.vertical ||
      tx[0] <= 0 ||
      tx[3] >= 0 ||
      Math.abs(tx[1]) > 0.01 ||
      Math.abs(tx[2]) > 0.01
    )
      return { reason: 'há texto girado, espelhado ou vertical' }
    if (![height, width].every((value) => Number.isFinite(value) && value > 0))
      return { reason: 'há dimensões de texto inválidas' }
    const fontSize = round((height * fit.scale) / MM_PER_POINT)
    if (fontSize < 6 || fontSize > 144)
      return {
        reason: 'há texto com tamanho fora do intervalo suportado pelo editor',
      }
    const baseline = fit.y + tx[5] * fit.scale
    const fontHeightMm = height * fit.scale
    runs.push({
      text: editableText.get(item),
      x: fit.x + tx[4] * fit.scale,
      baseline,
      y: baseline - fontHeightMm * 0.83,
      width: width * fit.scale,
      height: Math.max(4, fontHeightMm * 1.12),
      fontSize,
      ...mappedFont,
      color: runColors?.[0] || '#000000',
      hasEOL: item.hasEOL,
    })
  }
  const lines = []
  for (const run of runs) {
    const previous = lines.at(-1)
    const gap = previous ? run.x - (previous.x + previous.width) : Infinity
    const sameStyle =
      previous &&
      ['fontFamily', 'bold', 'italic', 'color'].every(
        (key) => previous[key] === run[key],
      ) &&
      Math.abs(previous.fontSize - run.fontSize) < 0.1
    if (
      previous &&
      !previous.hasEOL &&
      sameStyle &&
      Math.abs(previous.baseline - run.baseline) < 0.25 &&
      gap >= -0.1 &&
      gap < run.fontSize * MM_PER_POINT * 0.9
    ) {
      const separator =
        gap > run.fontSize * MM_PER_POINT * 0.12 &&
        !/\s$/.test(previous.text) &&
        !/^\s/.test(run.text)
          ? ' '
          : ''
      previous.text += separator + run.text
      previous.width = run.x + run.width - previous.x
      previous.hasEOL = run.hasEOL
    } else lines.push({ ...run })
  }
  if (lines.length > 100 - Number(reserveBackground))
    return { reason: 'a conversão ultrapassaria 100 elementos nesta página' }
  const elements = []
  for (const line of lines) {
    const width = Math.max(4, line.width + 0.5)
    if (
      line.text.length > 20000 ||
      line.x < 0 ||
      line.y < 0 ||
      line.x + width > PAGE_WIDTH ||
      line.y + line.height > PAGE_HEIGHT
    )
      return {
        reason:
          'um bloco de texto ficaria fora da página ou excederia o limite do editor',
      }
    elements.push(
      createElement('text', {
        ...line,
        x: round(line.x),
        y: round(line.y),
        width: Math.min(round(width), PAGE_WIDTH - round(line.x)),
        height: Math.min(round(line.height), PAGE_HEIGHT - round(line.y)),
        fontSize: round(line.fontSize),
        lineHeight: 1,
        alignment: 'left',
        locked: false,
      }),
    )
  }
  return { reason: null, elements, warnings: [...warnings] }
}
